import React, { lazy, Suspense, useState } from 'react';
import { Thermometer, ChevronDown, ChevronUp, Flame, Snowflake, Wind, Leaf } from 'lucide-react';
import { useMeteoConseil } from '../../hooks/useMeteoConseil';

const GardenWeatherPowerhouseV2 = lazy(() => import('./GardenWeatherPowerhouseV2'));

const STATUS = {
  ideal: { label: 'Idéal', dot: 'bg-emerald-500', pill: 'bg-emerald-100 text-emerald-800' },
  vigilance: { label: 'Vigilance', dot: 'bg-amber-500', pill: 'bg-amber-100 text-amber-800' },
  suspendu: { label: 'Suspendu', dot: 'bg-red-500', pill: 'bg-red-100 text-red-800' },
};

// Condensed horizontal "Garden Status Strip" for zone pages.
// Shows current temp + status + Canicule/Gel/Vent alert; full engine behind a toggle.
const GardenStatusStrip = ({ cityName = 'Coëx', lat = 46.6983, lon = -1.7633 }) => {
  const { conseil } = useMeteoConseil(lat, lon);
  const [showDetails, setShowDetails] = useState(false);

  const status = conseil ? STATUS[conseil.status] : STATUS.ideal;

  // Inline alert (Canicule / Gel / Vent), highest priority first
  let alert = null;
  if (conseil?.alerts?.canicule) {
    alert = {
      icon: <Flame size={16} className="text-amber-600 shrink-0" />,
      text: 'Chaleur : je décale mes tontes pour préserver votre pelouse.',
      cls: 'bg-amber-50 border-amber-200 text-amber-900',
    };
  } else if (conseil?.alerts?.gel) {
    alert = {
      icon: <Snowflake size={16} className="text-blue-600 shrink-0" />,
      text: 'Gel : pensez à la mise en hivernage de vos plantes fragiles.',
      cls: 'bg-blue-50 border-blue-200 text-blue-900',
    };
  } else if (conseil?.alerts?.vent) {
    alert = {
      icon: <Wind size={16} className="text-orange-600 shrink-0" />,
      text: 'Vent fort : je reporte les tailles de haies par sécurité.',
      cls: 'bg-orange-50 border-orange-200 text-orange-900',
    };
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="garden-status-strip">
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 px-4 py-3">
        {/* Title */}
        <div className="flex items-center gap-2 text-gray-900 font-bold">
          <Leaf className="w-5 h-5 text-emerald-600" />
          <span className="text-sm">Mon jardin à {cityName}</span>
        </div>

        {/* Current temp */}
        <div className="flex items-center gap-1.5 text-gray-800" data-testid="garden-strip-temp">
          <Thermometer className="w-4 h-4 text-orange-400" />
          <span className="font-bold">
            {conseil ? Math.round(conseil.temp) : '--'}°C
          </span>
        </div>

        {/* Status pill */}
        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold ${status.pill}`} data-testid="garden-strip-status">
          <span className={`w-2.5 h-2.5 rounded-full ${status.dot} animate-pulse`} />
          {status.label}
        </div>

        {/* Toggle */}
        <button
          onClick={() => setShowDetails((v) => !v)}
          className="ml-auto inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 text-sm font-semibold"
          data-testid="garden-strip-toggle"
          aria-expanded={showDetails}
        >
          Détails météo complets
          {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Inline alert banner */}
      {alert && (
        <div className={`flex items-center gap-2 px-4 py-2 border-t text-sm font-medium ${alert.cls}`} data-testid="garden-strip-alert">
          {alert.icon}
          <span>{alert.text}</span>
        </div>
      )}

      {/* Full engine, hidden by default */}
      {showDetails && (
        <div className="border-t border-gray-100 p-3 sm:p-4 bg-gray-50" data-testid="garden-strip-details">
          <Suspense fallback={
            <div className="bg-white rounded-2xl shadow p-8 animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
              <div className="h-64 bg-gray-100 rounded-xl" />
            </div>
          }>
            <GardenWeatherPowerhouseV2 cityName={cityName} />
          </Suspense>
        </div>
      )}
    </div>
  );
};

export default GardenStatusStrip;
